import { createFileRoute, Link, Outlet, useMatchRoute } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { PageHero } from "@/components/site/Layout";
import { EventEmptyState } from "@/components/site/EventEmptyState";
import { initials } from "@/components/site/data";
import { fetchEventCollection } from "@/lib/api";
import { useSelectedEvent } from "@/lib/event-context";
import { eventLabel, resolveSpeakerImageUrl } from "@/lib/events";
import { useState, useEffect } from "react";

interface Speaker {
  id: number;
  name: string;
  role: string;
  about: string | null;
  description: string | null;
  image_url: string | null;
}

export const Route = createFileRoute("/speakers")({
  component: Speakers,
  head: () => ({
    meta: [
      { title: "Speakers — Arab Capital Markets Conference" },
      {
        name: "description",
        content: "Meet the regulators, executives and experts speaking at the conference.",
      },
      { property: "og:title", content: "Conference Speakers" },
      { property: "og:description", content: "Leading voices in Arab capital markets." },
    ],
  }),
});

function Speakers() {
  const { selectedEvent, selectedEventId } = useSelectedEvent();
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [loading, setLoading] = useState(true);
  const matchRoute = useMatchRoute();

  useEffect(() => {
    if (!selectedEventId) {
      setSpeakers([]);
      setLoading(false);
      return;
    }

    const loadSpeakers = async () => {
      setLoading(true);
      try {
        const items = await fetchEventCollection<Speaker>(
          `/api/events/${selectedEventId}/speakers`,
          "/api/speakers",
        );
        setSpeakers(items);
      } catch (error) {
        console.error("Failed to load speakers:", error);
        setSpeakers([]);
      } finally {
        setLoading(false);
      }
    };

    loadSpeakers();
  }, [selectedEventId]);

  // If we're on a child route (speaker detail), render the Outlet
  const isChildRoute = matchRoute({ to: '/speakers/$speakerId' });
  if (isChildRoute) {
    return <Outlet />;
  }

  if (loading) {
    return (
      <>
        <PageHero
          eyebrow=""
          title="Speakers"
          subtitle="Leaders, regulators and experts from across the region and beyond"
        />
        <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center text-muted-foreground">Loading...</div>
        </section>
      </>
    );
  }

  return (
    <>
      <PageHero
        
        title="Speakers"
        subtitle={selectedEvent ? eventLabel(selectedEvent) : "Leaders, regulators and experts from across the region and beyond"}
      />
      {!selectedEvent ? (
        <EventEmptyState title="Choose an event" message="Select an event in the header to see its speakers." />
      ) : speakers.length === 0 ? (
        <EventEmptyState title="No speakers yet" message="Speakers have not been assigned to this event." />
      ) : (
      <section className="py-12 sm:py-2 md:py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5 md:gap-6">
          {speakers.map((s) => (
            <Link
              key={s.id}
              to="/speakers/$speakerId"
              params={{ speakerId: s.id.toString() }}
              className="group block"
            >
              <Card className="group overflow-hidden hover:shadow-2xl transition-all hover:-translate-y-1 duration-300 p-0 flex flex-col cursor-pointer">
                <div className="aspect-square bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center overflow-hidden">
                  {resolveSpeakerImageUrl(s.image_url) ? (
                    <img
                      src={resolveSpeakerImageUrl(s.image_url)!}
                      alt={s.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="font-serif text-3xl sm:text-4xl md:text-5xl text-accent/90">
                      {initials(s.name)}
                    </div>
                  )}
                </div>
                <div className="p-3 sm:p-4 md:p-5 flex-1 flex flex-col">
                  <h3 className="font-serif text-xs sm:text-sm md:text-base text-primary leading-snug mb-1.5 group-hover:text-accent transition-colors">
                    {s.name}
                  </h3>
                  <p className="text-[10px] sm:text-xs text-muted-foreground leading-relaxed mb-3">
                    {s.role}
                  </p>
                  <div className="mt-auto inline-flex items-center text-primary group-hover:text-accent transition-colors">
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </section>
      )}
    </>
  );
}
